import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHeader, TableRow, TableHead } from "@/components/ui/table";
import { EmptyState } from "@/components/EmptyState";
import { Megaphone, MoreVertical, Plus, Send } from "lucide-react";
import { usePromotions } from "@/hooks/sms/usePromotions";
import { usePromotionMutations } from "@/hooks/sms/usePromotionMutations";
import { PromotionWizard } from "@/pages/marketing/PromotionWizard";
import KillSwitchBanner from "@/components/marketing/KillSwitchBanner";
import StopMarketingButton from "@/components/marketing/StopMarketingButton";
import type { Promotion, PromotionChannelKind, PromotionInput, PromotionStatus } from "@/types/sms";

const CHANNEL_LABEL: Record<PromotionChannelKind, string> = {
  phone: "Phone",
  in_store: "In store",
  online: "Online",
};

interface PromotionsProps {
  storeId: string;
  /** Opens the Campaign tab's send composer for a promotion (null = let the
   *  owner pick one in the composer). */
  onLaunch: (promotionId: string | null) => void;
}

/** The promotions list + wizard, mounted as the Campaign tab of /marketing. */
export default function Promotions({ storeId, onLaunch }: PromotionsProps) {
  const [status, setStatus] = useState<PromotionStatus>("active");
  const [search, setSearch] = useState("");
  const { data: promotions = [], isLoading } = usePromotions({ status, search });
  const { createPromotion, updatePromotion, archivePromotion, unarchivePromotion } = usePromotionMutations();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Promotion | null>(null);

  const openCreate = () => {
    setEditing(null);
    setWizardOpen(true);
  };
  const openEdit = (promotion: Promotion) => {
    setEditing(promotion);
    setWizardOpen(true);
  };
  const openLaunch = (promotion?: Promotion) => {
    onLaunch(promotion?.id ?? null);
  };

  const handleSubmit = async (input: PromotionInput) => {
    if (editing) await updatePromotion(editing.id, input);
    else await createPromotion(input);
  };

  return (
    <div>
      <div className="mb-4 space-y-3">
        <KillSwitchBanner />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Input
            className="h-11 sm:max-w-xs"
            placeholder="Search promotions"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <StopMarketingButton />
            <Button variant="outline" className="h-11 self-start" onClick={() => openLaunch()}>
              <Send className="mr-2 h-4 w-4" />
              Launch campaign
            </Button>
            <Button className="h-11 self-start" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New promotion
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={status} onValueChange={(v) => setStatus(v as PromotionStatus)}>
        <TabsList className="mb-4">
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="archived">Archived</TabsTrigger>
        </TabsList>
        <TabsContent value={status}>
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : promotions.length === 0 ? (
            <EmptyState icon={Megaphone} title="No promotions yet" description="Create your first promotion to start issuing offers.">
              <Button className="mt-4 h-11" onClick={openCreate}>
                New promotion
              </Button>
            </EmptyState>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Promotion</TableHead>
                    <TableHead>Promo codes</TableHead>
                    <TableHead className="text-right">Estimated value</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promotions.map((promotion) => (
                    <TableRow key={promotion.id}>
                      <TableCell>
                        <div className="font-medium">{promotion.title}</div>
                        {promotion.description && (
                          <div className="line-clamp-1 text-xs text-muted-foreground">{promotion.description}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {promotion.channels.filter((c) => c.enabled).length === 0 ? (
                            <span className="text-xs text-muted-foreground">No code</span>
                          ) : (
                            promotion.channels
                              .filter((c) => c.enabled)
                              .map((c) => (
                                <Badge key={c.channel} variant="secondary">
                                  {CHANNEL_LABEL[c.channel]} {c.promo_code ?? ""}
                                </Badge>
                              ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        ${Number(promotion.estimated_value).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(promotion)}>Edit</DropdownMenuItem>
                            {promotion.status === "active" && (
                              <DropdownMenuItem onClick={() => openLaunch(promotion)}>Launch campaign</DropdownMenuItem>
                            )}
                            {promotion.status === "active" ? (
                              <DropdownMenuItem onClick={() => setArchiveTarget(promotion)}>Archive</DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => unarchivePromotion(promotion.id)}>Unarchive</DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <PromotionWizard
        open={wizardOpen}
        storeId={storeId}
        promotion={editing}
        onClose={() => setWizardOpen(false)}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this promotion?</AlertDialogTitle>
            <AlertDialogDescription>
              Archived promotions are hidden from new campaigns but issued offers keep working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (archiveTarget) archivePromotion(archiveTarget.id);
                setArchiveTarget(null);
              }}
            >
              Archive anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
